/**
 * Vocabulaire du « ça a bougé, et dans quel sens ».
 *
 * Extrait de `lib/markers.ts` le jour où les tuiles de KPI ont eu besoin
 * d'annoncer une variation : un chiffre en hausse doit se lire pareil qu'il
 * vienne d'un marqueur ou d'un taux de pénétration — même flèche, même
 * couleur, mêmes mots. Deux vocabulaires concurrents auraient donné deux
 * verts différents sur la même page.
 *
 * `lib/markers.ts` en ré-exporte tout : les imports existants n'ont pas bougé.
 */

/**
 * Sens du mouvement constaté.
 *
 * Une DIRECTION, pas un jugement : c'est `impactTone` qui traduit en
 * bénéfique / défavorable, parce que la traduction dépend de la métrique.
 */
export type ImpactVerdict = 'up' | 'down' | 'flat' | 'insufficient';

export type ImpactTone = 'good' | 'bad' | 'neutral';

/**
 * Direction → jugement.
 *
 * Séparé du verdict à dessein : « en hausse » n'est bénéfique que si la
 * métrique va dans ce sens-là. Toutes les métriques câblées aujourd'hui sont
 * en `higherIsBetter`, mais un taux d'erreur ou un churn n'aura qu'à passer
 * `false` plutôt qu'à réécrire le calcul.
 */
export function impactTone(verdict: ImpactVerdict, higherIsBetter: boolean | null): ImpactTone {
  // `null` : une métrique sans bon sens de variation (une dépense pub qui
  // monte n'est ni une bonne ni une mauvaise nouvelle en soi). La flèche dit
  // la direction, la couleur reste neutre.
  if (higherIsBetter === null) return 'neutral';
  if (verdict === 'up') return higherIsBetter ? 'good' : 'bad';
  if (verdict === 'down') return higherIsBetter ? 'bad' : 'good';
  return 'neutral';
}

/**
 * Libellé du verdict, en clair.
 *
 * Toujours disponible à côté de la couleur : un verdict qui ne reposerait que
 * sur du vert ou du rouge serait illisible en vision des couleurs déficiente,
 * et c'est une règle de tout le dashboard.
 */
export function verdictLabel(verdict: ImpactVerdict): string {
  switch (verdict) {
    case 'up':
      return 'en hausse';
    case 'down':
      return 'en baisse';
    case 'flat':
      return 'stable';
    case 'insufficient':
      return 'pas assez de recul';
  }
}

/** Flèche du verdict. Redondante avec le libellé, jamais seule porteuse de sens. */
export function verdictGlyph(verdict: ImpactVerdict): string {
  switch (verdict) {
    case 'up':
      return '▲';
    case 'down':
      return '▼';
    case 'flat':
      return '→';
    case 'insufficient':
      return '·';
  }
}

/**
 * Verdict d'une variation exprimée en POINTS de pourcentage.
 *
 * Sert aux taux, où l'écart se lit en points et non en variation relative : un
 * taux de pénétration qui passe de 21 % à 22,2 % a gagné 1,2 point, et
 * l'annoncer « +5,7 % » se ferait lire comme 5,7 points par la moitié des
 * lecteurs.
 *
 * `neutral` vaut un dixième de point par défaut : les taux sont calculés à
 * deux décimales sur des snapshots quotidiens, et en dessous de ce seuil on lit
 * l'arrondi, pas un mouvement. Un `null` en entrée — aucun snapshot pour
 * comparer — ressort `insufficient`, jamais « stable » : ne pas savoir n'est
 * pas la même chose que ne pas bouger.
 */
export function pointsVerdict(points: number | null, neutral = 0.1): ImpactVerdict {
  if (points === null) return 'insufficient';
  if (Math.abs(points) < neutral) return 'flat';
  return points > 0 ? 'up' : 'down';
}

/**
 * Verdict d'une variation RELATIVE, en %.
 *
 * Sert aux volumes (chiffre d'affaires, commandes, unités), où l'écart se lit
 * en relatif. Sous `neutral` % on lit du bruit — une commande de plus sur
 * deux cents — et la ligne reste neutre. Un `null` — pas de référence, ou une
 * référence à zéro — ressort `insufficient`, jamais « stable ».
 */
export function relativeVerdict(relative: number | null, neutral = 2): ImpactVerdict {
  if (relative === null) return 'insufficient';
  if (Math.abs(relative) < neutral) return 'flat';
  return relative > 0 ? 'up' : 'down';
}
